import { destination, point, multiPolygon, type Units } from '@turf/turf';
import type { Coordinate } from '../coordinate';
import { VehicleSymbol } from '../../../vehicles/domain/vehicleSymbol';
import { arrowShape } from './arrowShape';
import { wheelLoaderFrontGrappleShape } from './wheelLoaderFrontGrappleShape';
import { wheelLoaderHighGrappleShape } from './wheelLoaderHighGrappleShape';
import { timberCraneShape } from './timberCraneShape';
import { coilForkliftShape } from './coilForkliftShape';
import { logStackerShape } from './logStackerShape';

export interface VehicleShapeOptions {
  coordinate: Coordinate;
  properties: GeoJSON.GeoJsonProperties;
  heading: number | null;
  symbol?: VehicleSymbol;
  anchorMode?: VehicleShapeAnchorMode;
  selectionPoint?: NormalizedPoint | null;
}

export type VehicleShapeAnchorMode = 'default' | 'workingTip' | 'selectionPoint';

export interface PolygonColorConfig {
  type: 'dynamic' | 'fixed';
  color?: string; // Used when type is 'fixed'
}

export interface VehicleShapeDefinition {
  vehicle: string;
  coordinateSystem: '2D';
  unit: 'normalized_percent';
  polygons: {
    [key: string]: Array<[number, number]>;
  };
  colorConfig?: {
    [polygonName: string]: PolygonColorConfig;
  };
}

export interface NormalizedPoint {
  x: number;
  y: number;
}

const turfOptions = { units: 'meters' as Units };

/**
 * Configuration for vehicle dimensions and origin points
 */
interface VehicleConfig {
  vehicleLength: number; // meters, along the Y-axis
  vehicleWidth: number; // meters, along the X-axis
  originX: number; // origin X in normalized space (0-100)
  originY: number; // origin Y in normalized space (0-100)
  workingTipX: number; // working tip X in normalized space (0-100)
  workingTipY: number; // working tip Y in normalized space (0-100)
  selectionPointX: number; // selectable anchor point X in normalized space (0-100)
  selectionPointY: number; // selectable anchor point Y in normalized space (0-100)
}

/**
 * Default vehicle configuration
 */
const defaultVehicleConfig: VehicleConfig = {
  vehicleLength: 6,
  vehicleWidth: 4,
  originX: 50,
  originY: 0, // Default arrow tip
  workingTipX: 50,
  workingTipY: 0,
  selectionPointX: 50,
  selectionPointY: 0,
};

/**
 * Vehicle-specific configurations
 * Maps VehicleSymbol to its dimensions and origin point
 * originY is set to the grapple/fork grab point for each vehicle
 */
const vehicleConfigs: Partial<Record<VehicleSymbol, VehicleConfig>> = {
  [VehicleSymbol.LogStacker]: {
    vehicleLength: 6,
    vehicleWidth: 4,
    originX: 50,
    originY: 6, // Grapple grab point
    workingTipX: 50,
    workingTipY: 0,
    selectionPointX: 50,
    selectionPointY: 0,
  },
  [VehicleSymbol.WheelLoaderFrontGrapple]: {
    vehicleLength: 6,
    vehicleWidth: 4,
    originX: 50,
    originY: 22, // Grapple grab point
    workingTipX: 50,
    workingTipY: 15,
    selectionPointX: 50,
    selectionPointY: 15,
  },
  [VehicleSymbol.WheelLoaderHighGrapple]: {
    vehicleLength: 6,
    vehicleWidth: 4,
    originX: 50,
    originY: 7, // Grapple grab point
    workingTipX: 50,
    workingTipY: 0,
    selectionPointX: 50,
    selectionPointY: 0,
  },
  [VehicleSymbol.TimberCrane]: {
    vehicleLength: 6,
    vehicleWidth: 4,
    originX: 50,
    originY: 7,
    workingTipX: 50,
    workingTipY: 0,
    selectionPointX: 50,
    selectionPointY: 0,
  },
  [VehicleSymbol.CoilForklift]: {
    vehicleLength: 6,
    vehicleWidth: 4,
    originX: 50,
    originY: 10, // Fork grab point (middle of fork)
    workingTipX: 50,
    workingTipY: 0,
    selectionPointX: 50,
    selectionPointY: 0,
  },
  [VehicleSymbol.Undefined]: {
    vehicleLength: 6,
    vehicleWidth: 4,
    originX: 50,
    originY: 0, // Arrow tip
    workingTipX: 50,
    workingTipY: 0,
    selectionPointX: 50,
    selectionPointY: 0,
  },
};

/**
 * Get the vehicle configuration for a symbol
 * @param symbol The vehicle symbol
 * @returns The vehicle configuration (dimensions and origin)
 */
function getVehicleConfig(symbol?: VehicleSymbol): VehicleConfig {
  if (symbol !== undefined && vehicleConfigs[symbol]) {
    return vehicleConfigs[symbol]!;
  }
  return defaultVehicleConfig;
}

function clampNormalized(value: number) {
  return Math.max(-100, Math.min(200, value));
}

export function getVehicleSelectionPoint(symbol: VehicleSymbol): NormalizedPoint {
  const config = getVehicleConfig(symbol);
  return {
    x: config.selectionPointX,
    y: config.selectionPointY,
  };
}

export function setVehicleSelectionPoint(symbol: VehicleSymbol, point: NormalizedPoint) {
  const config = getVehicleConfig(symbol);
  config.selectionPointX = clampNormalized(point.x);
  config.selectionPointY = clampNormalized(point.y);
}

/** Minimal vehicle anchor/pickup configuration shared by Vehicle and MyVehicle. */
export interface VehicleAnchorConfig {
  symbol: VehicleSymbol;
  selectionAnchorPointX: number | null;
  selectionAnchorPointY: number | null;
  usePickupPoint: boolean;
  pickupPointX: number | null;
  pickupPointY: number | null;
  anchorRotationOffset: number;
}

/** A geographic position that the vehicle icon is anchored to. */
export interface ReferencePositionInput {
  latitude: number;
  longitude: number;
  heading?: number | null;
}

export interface ReferencePosition {
  latitude: number;
  longitude: number;
  heading: number;
}

function normalizeDegrees(value: number): number {
  return ((value % 360) + 360) % 360;
}

/** Effective anchor point (explicit selection point or the symbol default). */
export function getEffectiveAnchorPoint(config: VehicleAnchorConfig): NormalizedPoint {
  if (config.selectionAnchorPointX !== null && config.selectionAnchorPointY !== null) {
    return { x: config.selectionAnchorPointX, y: config.selectionAnchorPointY };
  }
  return getVehicleSelectionPoint(config.symbol);
}

/** Reference point on the icon: the pickup point when enabled and set, otherwise the anchor. */
export function getEffectivePickupPoint(config: VehicleAnchorConfig): NormalizedPoint {
  if (config.usePickupPoint && config.pickupPointX !== null && config.pickupPointY !== null) {
    return { x: config.pickupPointX, y: config.pickupPointY };
  }
  return getEffectiveAnchorPoint(config);
}

/**
 * Resolve the geographic point from which proximity/area checks start.
 * The GPS position is anchored at the selection anchor point; when a pickup point
 * is active the reference is offset by the icon-space delta (pickup - anchor),
 * rotated by heading + anchorRotationOffset. Returns null when position is null.
 */
export function getVehicleReferencePosition(
  config: VehicleAnchorConfig,
  position: ReferencePositionInput | null,
): ReferencePosition | null {
  if (!position) return null;

  const dir = normalizeDegrees((position.heading ?? 0) + (config.anchorRotationOffset || 0));

  const anchor = getEffectiveAnchorPoint(config);
  const pickup = getEffectivePickupPoint(config);

  if (anchor.x === pickup.x && anchor.y === pickup.y) {
    return { latitude: position.latitude, longitude: position.longitude, heading: dir };
  }

  const { vehicleLength, vehicleWidth } = getVehicleConfig(config.symbol);
  const relX = ((pickup.x - anchor.x) / 100) * vehicleWidth;
  const relY = ((anchor.y - pickup.y) / 100) * vehicleLength;

  let geoPoint = point([position.longitude, position.latitude]);
  if (relY !== 0) {
    geoPoint = destination(geoPoint, relY, dir, turfOptions);
  }
  if (relX !== 0) {
    geoPoint = destination(geoPoint, relX, dir + 90, turfOptions);
  }

  const [longitude, latitude] = geoPoint.geometry.coordinates;
  return { latitude: latitude!, longitude: longitude!, heading: dir };
}

function getOrigin(
  config: VehicleConfig,
  anchorMode: VehicleShapeAnchorMode,
  selectionPoint?: NormalizedPoint | null,
) {
  if (anchorMode === 'workingTip') {
    return {
      originX: config.workingTipX,
      originY: config.workingTipY,
    };
  }

  if (anchorMode === 'selectionPoint') {
    if (selectionPoint) {
      return {
        originX: clampNormalized(selectionPoint.x),
        originY: clampNormalized(selectionPoint.y),
      };
    }

    return {
      originX: config.selectionPointX,
      originY: config.selectionPointY,
    };
  }

  return {
    originX: config.originX,
    originY: config.originY,
  };
}

/**
 * Get the shape definition for a vehicle symbol
 * @param symbol The vehicle symbol (defaults to arrow shape if undefined)
 * @returns The corresponding shape definition
 */
function getShapeDefinition(symbol?: VehicleSymbol): VehicleShapeDefinition {
  switch (symbol) {
    case VehicleSymbol.LogStacker:
      return logStackerShape;
    case VehicleSymbol.WheelLoaderFrontGrapple:
      return wheelLoaderFrontGrappleShape;
    case VehicleSymbol.WheelLoaderHighGrapple:
      return wheelLoaderHighGrappleShape;
    case VehicleSymbol.TimberCrane:
      return timberCraneShape;
    case VehicleSymbol.CoilForklift:
      return coilForkliftShape;
    case VehicleSymbol.Undefined:
    default:
      return arrowShape;
  }
}

/**
 * Creates a vehicle shape from a shape definition with normalized coordinates
 * Origin is positioned at the grapple/fork grab point for each vehicle type
 * This ensures loads are consistently positioned at the grapple regardless of vehicle symbol
 */
export function createVehicleShape(
  options: VehicleShapeOptions,
  shapeDefinition?: VehicleShapeDefinition,
) {
  const {
    coordinate,
    heading,
    properties,
    symbol,
    anchorMode = 'default',
    selectionPoint,
  } = options;
  const { longitude, latitude } = coordinate;

  // If no shape definition provided, get it from the symbol
  const shape = shapeDefinition ?? getShapeDefinition(symbol);

  // Get vehicle configuration (dimensions and origin) based on symbol
  const config = getVehicleConfig(symbol);
  const { vehicleLength, vehicleWidth } = config;
  const { originX, originY } = getOrigin(config, anchorMode, selectionPoint);

  const dir = heading ?? 0;
  const origin = point([longitude, latitude]);

  // Store polygon names and color config for use in styling
  const polygonNames = Object.keys(shape.polygons);
  const colorConfig = shape.colorConfig || {};

  // Convert normalized coordinates to geo coordinates
  // Origin is at (50, 8) in normalized space, which is the grapple center
  const normalizedPolygons = Object.entries(shape.polygons).map(([name, coords]) => {
    const geoCoordinates = coords.map(([x, y]) => {
      // Convert from normalized (0-100) to relative position from origin
      // X: 0 = left edge, 50 = center, 100 = right edge
      // Y: 0 = front, 100 = back
      const relX = ((x - originX) / 100) * vehicleWidth;
      const relY = ((originY - y) / 100) * vehicleLength;

      // Apply transformations: forward (0°), then sideways (90°)
      let geoPoint = origin;

      // Move forward along heading direction
      if (relY !== 0) {
        geoPoint = destination(geoPoint, relY, dir, turfOptions);
      }

      // Move sideways (perpendicular to heading)
      if (relX !== 0) {
        geoPoint = destination(geoPoint, relX, dir + 90, turfOptions);
      }

      return geoPoint.geometry.coordinates;
    });

    // Close the polygon by adding the first point at the end
    geoCoordinates.push(geoCoordinates[0]!);
    return [geoCoordinates]; // Wrap in array for polygon rings
  });

  // Create a MultiPolygon feature containing all parts
  // Add polygon metadata to properties for styling
  const enhancedProperties = {
    ...properties,
    polygonNames,
    colorConfig,
  };

  const multiPoly = multiPolygon(normalizedPolygons, enhancedProperties);

  return multiPoly;
}
